import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Collapse, Divider, List, ListItem, ListItemText } from '@mui/material';
import { useState } from 'react';
import type { GuideGateStatus, GuidePublishGates } from '../../services/api-client';
import GuideFactCheckFindings from '../GuideFactCheckFindings';

const GATE_STATUS_COLORS: Record<GuideGateStatus, 'success' | 'error' | 'warning'> = {
  pass: 'success',
  fail: 'error',
  advisory: 'warning',
};

const GATE_STATUS_LABELS: Record<GuideGateStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
  advisory: 'Advisory',
};

/** The publish checklist, one row per gate (the same rows the guide detail screen shows). */
export function GuideGateChecklist({
  gates,
  slug,
  onGatesChanged,
}: {
  gates: GuidePublishGates;
  slug: string;
  /** Called after a reviewer dismisses or restores a fact-check finding. */
  onGatesChanged: () => Promise<void> | void;
}) {
  return (
    <List dense disablePadding data-testid="editor-gate-list">
      {gates.gates.map((g, idx) => (
        <Box key={g.gate}>
          <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
            <Chip
              label={GATE_STATUS_LABELS[g.status]}
              color={GATE_STATUS_COLORS[g.status]}
              size="small"
              variant={g.status === 'advisory' ? 'outlined' : 'filled'}
              sx={{ mr: 1.5, mt: 0.25, minWidth: 74 }}
            />
            <ListItemText
              primary={g.label}
              secondary={g.detail}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
          {g.gate === 'fact_check' && gates.factCheck && (
            <GuideFactCheckFindings
              slug={slug}
              factCheck={gates.factCheck}
              isReviewer={gates.me.reviewer !== null}
              onChanged={onGatesChanged}
            />
          )}
          {idx < gates.gates.length - 1 && <Divider component="li" />}
        </Box>
      ))}
    </List>
  );
}

/** "4 of 6 checks pass", expanding to the full checklist. */
export default function GuideGateSummary({
  gates,
  error,
  slug,
  onGatesChanged,
}: {
  gates: GuidePublishGates | null;
  error: string | null;
  slug: string;
  onGatesChanged: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  if (!gates) {
    return error ? (
      <Alert severity="warning" sx={{ py: 0 }}>
        {error}
      </Alert>
    ) : null;
  }
  const passing = gates.gates.filter((g) => g.status === 'pass').length;
  const failing = gates.gates.some((g) => g.status === 'fail');
  return (
    <Box>
      <Button
        size="small"
        color={failing ? 'warning' : 'success'}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setOpen((o) => !o)}
        data-testid="gate-summary"
      >
        {passing} of {gates.gates.length} checks pass
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <GuideGateChecklist gates={gates} slug={slug} onGatesChanged={onGatesChanged} />
        </Box>
      </Collapse>
    </Box>
  );
}
